// RecentRblActivations — on-chain social proof: last N wallets that paid PRO
// with R₿L. Public, anonymized, verifiable on Basescan via tx_hash link.
import React, { useEffect, useState } from "react";
import { ExternalLink, Sparkles, Loader2 } from "lucide-react";
import { API } from "../../lib/api";
import { useLanguage } from "../../i18n/LanguageContext";

const T = {
  es: {
    title: "Últimas activaciones PRO con R₿L",
    subtitle: "100% on-chain · verificable en Basescan",
    empty: "Sé el primero en activar PRO con R₿L.",
    ago_s: "hace {n}s",
    ago_m: "hace {n} min",
    ago_h: "hace {n} h",
    ago_d: "hace {n} d",
  },
  en: {
    title: "Latest PRO activations with R₿L",
    subtitle: "100% on-chain · verifiable on Basescan",
    empty: "Be the first to activate PRO with R₿L.",
    ago_s: "{n}s ago",
    ago_m: "{n} min ago",
    ago_h: "{n} h ago",
    ago_d: "{n} d ago",
  },
  sk: {
    title: "Posledné aktivácie PRO s R₿L",
    subtitle: "100% on-chain · overiteľné na Basescan",
    empty: "Buď prvý, kto aktivuje PRO s R₿L.",
    ago_s: "pred {n}s",
    ago_m: "pred {n} min",
    ago_h: "pred {n} h",
    ago_d: "pred {n} d",
  }, de: { title: "Neueste PRO-Aktivierungen mit R₿L", subtitle: "100% on-chain · verifizierbar auf Basescan", empty: "Sei der Erste, der PRO mit R₿L aktiviert.", ago_s: "vor {n}s", ago_m: "vor {n} Min.", ago_h: "vor {n} Std.", ago_d: "vor {n} T." }, "zh-CN": { title: "最新 R₿L PRO 激活记录", subtitle: "100% 链上 · 可在 Basescan 验证", empty: "成为首位使用 R₿L 激活 PRO 的用户。", ago_s: "{n}秒前", ago_m: "{n}分钟前", ago_h: "{n}小时前", ago_d: "{n}天前" },
};

const short = (w) => (w ? `${w.slice(0, 6)}…${w.slice(-4)}` : "—");

const timeAgo = (iso, t) => {
  if (!iso) return "";
  const diff = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (diff < 60) return t.ago_s.replace("{n}", diff);
  if (diff < 3600) return t.ago_m.replace("{n}", Math.floor(diff / 60));
  if (diff < 86400) return t.ago_h.replace("{n}", Math.floor(diff / 3600));
  return t.ago_d.replace("{n}", Math.floor(diff / 86400));
};

const fmt = (n) => Number(n || 0).toLocaleString();

export default function RecentRblActivations({ limit = 5 }) {
  const { lang } = useLanguage();
  const t = T[lang] || T.en || T.es;
  const [items, setItems] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = () =>
      fetch(`${API}/pro/recent-activations?limit=${limit}`)
        .then((r) => r.json())
        .then((d) => { if (alive) setItems(d.items || []); })
        .catch(() => { if (alive) setItems([]); });
    load();
    const id = setInterval(load, 30_000);
    return () => { alive = false; clearInterval(id); };
  }, [limit]);

  return (
    <div
      className="rounded-2xl border border-violet-400/25 bg-gradient-to-br from-violet-500/[0.04] to-orange-500/[0.03] p-5"
      data-testid="recent-rbl-activations"
    >
      <div className="flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-violet-300" />
        <span className="font-mono text-[11px] font-black uppercase tracking-[0.22em] text-white">
          {t.title}
        </span>
      </div>
      <p className="mt-0.5 text-[10px] text-white/40">{t.subtitle}</p>

      {items === null ? (
        <div className="mt-4 flex items-center justify-center py-4">
          <Loader2 className="h-4 w-4 animate-spin text-white/30" />
        </div>
      ) : items.length === 0 ? (
        <p className="mt-4 text-center text-xs text-white/45" data-testid="recent-rbl-empty">
          {t.empty}
        </p>
      ) : (
        <ul className="mt-3 space-y-2" data-testid="recent-rbl-list">
          {items.map((it, i) => (
            <li
              key={it.tx_hash || i}
              className="flex flex-wrap items-center gap-2 rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2.5"
              data-testid={`recent-rbl-item-${i}`}
            >
              <span className="font-mono text-xs font-bold text-white">
                {short(it.wallet)}
              </span>
              <span className="rounded-full border border-emerald-400/30 bg-emerald-400/[0.06] px-2 py-0.5 font-mono text-[10px] font-black text-emerald-300">
                PRO
              </span>
              <span className="ml-auto font-mono text-xs font-black text-amber-300">
                {fmt(it.amount_rbl)} R₿L
              </span>
              <span className="font-mono text-[10px] text-white/35">
                {timeAgo(it.activated_at, t)}
              </span>
              {it.tx_hash && (
                <a
                  href={`https://basescan.org/tx/${it.tx_hash}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-white/35 transition-colors hover:text-violet-300"
                  data-testid={`recent-rbl-tx-${i}`}
                  aria-label="Basescan"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
